import React from 'react';
import { ArrowRight, CheckCircle, Star, TrendingUp } from 'lucide-react';
import { Link } from 'react-router-dom';
import * as LucideIcons from 'lucide-react';
import { heroData, services, whyChooseUs, testimonials, stats, contactInfo } from '../data/mock';

const Home = () => {
  const IconComponent = ({ iconName }) => {
    const Icon = LucideIcons[iconName.split('-').map((word, i) => 
      i === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1)
    ).join('').replace(/^\w/, c => c.toUpperCase())];
    return Icon ? <Icon size={24} /> : <Star size={24} />;
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-[#0D234E] via-[#1a3a6e] to-[#0D234E] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-[#BFB47E] rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#BFB47E] rounded-full filter blur-3xl"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-block bg-[#BFB47E] bg-opacity-20 px-4 py-2 rounded-full mb-6">
                <span className="text-[#BFB47E] font-semibold text-sm">Digital Marketing Excellence</span>
              </div>
              <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
                {heroData.title}
              </h1>
              <p className="text-2xl text-[#BFB47E] font-serif mb-4">
                {heroData.subtitle}
              </p>
              <p className="text-lg text-gray-300 mb-8 leading-relaxed">
                {heroData.description}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/contact"
                  className="bg-[#BFB47E] text-[#0D234E] px-8 py-4 rounded-md font-semibold hover:bg-[#d4c991] transition-all duration-200 hover:shadow-xl hover:scale-105 flex items-center justify-center"
                >
                  {heroData.ctaPrimary}
                  <ArrowRight size={20} className="ml-2" />
                </Link>
                <a
                  href={contactInfo.whatsapp}
                  data-testid="home-hero-whatsapp"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-md font-semibold hover:bg-white hover:text-[#0D234E] transition-all duration-200 flex items-center justify-center"
                >
                  {heroData.ctaSecondary}
                </a>
              </div>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-[#BFB47E] rounded-2xl transform rotate-6 opacity-20"></div>
              <img
                src={heroData.heroImage}
                alt="Digital Marketing Success"
                className="relative z-10 rounded-2xl shadow-2xl w-full h-[500px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="flex justify-center mb-3">
                  <div className="w-14 h-14 bg-[#0D234E] bg-opacity-10 rounded-full flex items-center justify-center">
                    <IconComponent iconName={stat.icon} />
                  </div>
                </div>
                <div className="text-4xl font-bold text-[#0D234E] mb-2">{stat.value}</div>
                <div className="text-gray-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0D234E] mb-4">
              Our Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive digital marketing solutions designed to drive growth, increase visibility, and maximize your ROI
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => (
              <div
                key={service.id}
                className="bg-white p-8 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border border-gray-100"
              >
                <div className="w-14 h-14 bg-[#0D234E] rounded-lg flex items-center justify-center text-[#BFB47E] mb-6">
                  <IconComponent iconName={service.icon} />
                </div>
                <h3 className="text-xl font-bold text-[#0D234E] mb-3">
                  {service.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {service.description}
                </p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/services"
              className="inline-flex items-center bg-[#0D234E] text-white px-8 py-4 rounded-md font-semibold hover:bg-[#1a3a6e] transition-all duration-200 hover:shadow-lg"
            >
              View All Services
              <ArrowRight size={20} className="ml-2" />
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0D234E] mb-4">
              Why Choose DMARKETINGCHAMP
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We're not just another agency—we're your dedicated growth partner
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyChooseUs.map((reason) => (
              <div
                key={reason.id}
                className="text-center p-6 rounded-xl hover:bg-gray-50 transition-colors duration-300"
              >
                <div className="flex justify-center mb-4">
                  <div className="w-16 h-16 bg-[#BFB47E] bg-opacity-20 rounded-full flex items-center justify-center">
                    <IconComponent iconName={reason.icon} />
                  </div>
                </div>
                <h3 className="text-xl font-bold text-[#0D234E] mb-3">
                  {reason.title}
                </h3>
                <p className="text-gray-600">
                  {reason.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0D234E] mb-4">
              What Our Clients Say
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Real results from real businesses
            </p>
            <Link to="/salesproof" data-testid="home-salesproof-link" className="mt-6 inline-flex items-center gap-2 font-semibold text-[#0D234E] hover:underline">
              View Sales Proof <ArrowRight size={18} aria-hidden="true" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {testimonials.map((testimonial) => (
              <div
                key={testimonial.id}
                className="bg-white p-8 rounded-xl shadow-md hover:shadow-xl transition-shadow duration-300"
              >
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} size={20} className="text-[#BFB47E] fill-current" />
                  ))}
                </div>
                <p className="text-gray-700 mb-6 italic leading-relaxed">
                  "{testimonial.content}"
                </p>
                <div className="flex items-center">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover mr-4"
                  />
                  <div>
                    <div className="font-bold text-[#0D234E]">{testimonial.name}</div>
                    <div className="text-sm text-gray-600">
                      {testimonial.role}, {testimonial.company}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#0D234E] to-[#1a3a6e] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Grow Your Business?
          </h2>
          <p className="text-xl mb-8 text-gray-300">
            Let's discuss how we can help you achieve your digital marketing goals
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-[#BFB47E] text-[#0D234E] px-8 py-4 rounded-md font-semibold hover:bg-[#d4c991] transition-all duration-200 hover:shadow-xl inline-flex items-center justify-center"
            >
              Get Your Free Consultation
              <ArrowRight size={20} className="ml-2" />
            </Link>
            <a
              href={contactInfo.whatsapp}
              data-testid="home-contact-whatsapp"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-md font-semibold hover:bg-white hover:text-[#0D234E] transition-all duration-200 inline-flex items-center justify-center"
            >
              Chat on WhatsApp
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
